import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getDateRangeValue, getRangeValue } from '../../../../packages/JsonQuery';
import { closeAndResetListingModal } from '../../../common/actions/listingModal';
import { onAddToFavorite as addToFavorites } from '../../../common/actions/newFavorites/onAddToFavorite';
import { removeFromFavorites } from '../../../common/actions/newFavorites/removeFromFavorite';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { useOffer } from '../../../common/containers/OfferCardContainer/OfferCardContext';
import { prepareOfferUrl } from '../../../common/containers/OfferCardContainer/utils/prepareOfferUrl';
import { useModalContext } from '../../../common/containers/SwipeModal/ModalContext/useModalContext';
import { useTrackingProps } from '../../../common/hooks/useTrackingProps';
import { selectResultsJsonQuery } from '../../../common/selectors/results';
import { TThunkDispatch } from '../../../common/types/redux';
import { defferedTrack } from '../../../common/utils/tracking';
import { OfferCardPreview } from '../../components/OfferCardPreview';
import {
  trackAddFavorite,
  trackBookClick,
  trackCall,
  trackCardClick,
  trackChatClick,
  trackShowPhoneClick,
  trackSlideChange,
} from '../../tracking';
import { trackMoreClick } from '../../tracking/trackMoreClick';
import { trackRemoveFromFavorite } from '../../tracking/trackRemoveFromFavorite';

export const OfferCardPreviewContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { config } = useApplicationContext();
  const isPartnerFavoritesEnabled = !!config.get<boolean>('dailyrent.partners_favorites.Enabled');
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const { offer } = useOffer();
  const isSliderEventTriggeredRef = React.useRef(false);
  const isFavoriteButtonVisible = (!offer.isCianPartner || isPartnerFavoritesEnabled) && !offer.isHotel;

  const jsonQueryDates = getDateRangeValue('dates')(jsonQuery);
  const checkInDate = jsonQueryDates?.gte;
  const checkOutDate = jsonQueryDates?.lt;
  const minBeds = getRangeValue('beds_count')(jsonQuery)?.gte;
  const hasDates = !!jsonQueryDates;

  const offerLink = prepareOfferUrl({
    offer,
    checkInDate,
    checkOutDate,
    minBeds,
  });

  const trackingProps = useTrackingProps({ offer, offerPosition: 1, offersLength: 1 });

  const onSlideChange = React.useCallback(() => {
    if (!isSliderEventTriggeredRef.current) {
      trackSlideChange(trackingProps);
      isSliderEventTriggeredRef.current = true;
    }
  }, [trackingProps]);

  const onCardClick = React.useCallback(() => {
    trackCardClick(trackingProps);
  }, [trackingProps]);

  const onAddToFavorites = React.useCallback(() => {
    dispatch(addToFavorites(offer.id));
    trackAddFavorite(trackingProps);
  }, [dispatch, offer.id, trackingProps]);

  const onRemoveFromFavorites = React.useCallback(() => {
    dispatch(removeFromFavorites(offer.id));
    trackRemoveFromFavorite(trackingProps);
  }, [dispatch, offer.id, trackingProps]);

  const onMoreClick = React.useCallback(() => {
    trackMoreClick(trackingProps);
  }, [trackingProps]);

  const onBookClick = React.useCallback(() => {
    trackBookClick(trackingProps);
  }, [trackingProps]);

  const onCallClick = React.useCallback(() => {
    trackShowPhoneClick(trackingProps);
    defferedTrack(() => trackCall(trackingProps));
  }, [trackingProps]);

  const onChatClick = React.useCallback(() => {
    trackChatClick(trackingProps);
  }, [trackingProps]);

  const { closeModal } = useModalContext();

  const onClose = React.useCallback(async () => {
    await closeModal();
    dispatch(closeAndResetListingModal());
  }, [closeModal, dispatch]);

  return (
    <OfferCardPreview
      offer={offer}
      offerLink={offerLink}
      showFavoriteButton={isFavoriteButtonVisible}
      hasDates={hasDates}
      onSlideChange={onSlideChange}
      onCardClick={onCardClick}
      onAddToFavorites={onAddToFavorites}
      onRemoveFromFavorites={onRemoveFromFavorites}
      onMoreClick={onMoreClick}
      onBookClick={onBookClick}
      onCallClick={onCallClick}
      onChatClick={onChatClick}
      onClose={onClose}
    />
  );
};
